import http from './httpClient';
import notificationsApi from './notificationsApi';

class InventoryService {
  // Reconcile an inventory item (negative stock correction / manual adjustment)
  static async reconcileItem(itemId, payload) {
    try {
      const data = await http.post(`/inventory/${itemId}/reconcile`, payload);
      if (!data.success) throw new Error(data.error || 'Failed to reconcile item');
      return data.data;
    } catch (error) {
      console.error('Error reconciling item:', error);
      throw error;
    }
  }

  // Delete inventory batch
  static async deleteInventoryBatch(itemId, batchId) {
    try {
      const data = await http.delete(`/inventory/items/${itemId}/batches/${batchId}`);
      if (!data.success) throw new Error(data.error || 'Failed to delete inventory batch');
      return data;
    } catch (error) {
      console.error('Error deleting inventory batch:', error);
      throw error;
    }
  }

  // Get all batches for an item
  static async getItemBatches(itemId) {
    try {
      const data = await http.get(`/inventory/items/${itemId}/batches`);
      if (!data.success) throw new Error(data.error || 'Failed to fetch item batches');
      return data;
    } catch (error) {
      console.error('Error fetching item batches:', error);
      throw error;
    }
  }

  // Get inventory overview (this is actually under stock-in routes)
  static async getInventoryOverview(businessId = null, opts = {}) {
    try {
      const bId = Number(
        businessId ?? (typeof window !== 'undefined' ? window.localStorage.getItem('businessId') : null)
      );
      const includeInactive = opts.includeInactive === undefined ? true : !!opts.includeInactive;
      const includeComplimentary = !!opts.includeComplimentary;
      const params = new URLSearchParams({ businessId: String(bId) });
      if (includeInactive) params.append('includeInactive', 'true');
      if (includeComplimentary) params.append('includeComplimentary', 'true');
      const data = await http.get(`/stock-in/v2/inventory/overview?${params.toString()}`);
      if (!data.success) throw new Error(data.error || 'Failed to fetch inventory overview');
      return data;
    } catch (error) {
      console.error('Error fetching inventory overview:', error);
      throw error;
    }
  }

  // Get inventory items (using unit-mapping endpoint since inventory/items doesn't exist)
  static async getInventoryItems(businessId, limit = 50) {
    try {
      const data = await http.get(`/unit-mapping/inventory-items/${businessId}?limit=${limit}`);
      if (!data.success) throw new Error(data.error || 'Failed to fetch inventory items');
      return data;
    } catch (error) {
      console.error('Error fetching inventory items:', error);
      throw error;
    }
  }

  // Search helper (client-side filter after fetching a page)
  static async searchInventoryItems(query, businessId, limit = 200) {
    const data = await this.getInventoryItems(businessId, limit);
    const items = data.data || [];
    if (!query) return items;
    const q = query.trim().toLowerCase();
    return items.filter((i) => {
      const nameMatch = i.name?.toLowerCase().includes(q);
      const aliasMatch = Array.isArray(i.aliases) && i.aliases.some((alias) => String(alias || '').toLowerCase().includes(q));
      return nameMatch || aliasMatch;
    });
  }

  // Create inventory item (upsert) with unit validation
  static async createInventoryItem({ name, unit_symbol = 'g', business_id, user_id, source = 'manual' }) {
    // Validate unit before creating item
    try {
      await notificationsApi.validateUnit(business_id, user_id, unit_symbol, name);
    } catch (error) {
      console.error('Unit validation error:', error);
      // Continue with creation even if validation fails - notification will be created
    }

    const payload = { name, unit_symbol, business_id, source };
    const data = await http.post('/inventory/items', payload);
    if (!data.success) throw new Error(data.error || 'Failed to create inventory item');
    return data.data;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // UIRA — Unified Inventory Reconciliation Algorithm
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Trigger UIRA month-end fix for one inventory item.
   * Called when the manager enters physical stock and clicks "Fix".
   *
   * @param {number} itemId
   * @param {{ physical_stock: number, month_key: string, user_id?: number }} payload
   */
  static async uiraFix(itemId, payload) {
    try {
      const data = await http.post(`/inventory/${itemId}/uira-fix`, payload);
      return data;
    } catch (error) {
      // Re-throw with structured error for UI handling
      const err = error?.data || error;
      throw err;
    }
  }

  /**
   * Get all pending unmapped POS categories for this business.
   * Used to show the warning banner.
   */
  static async getUnmappedPOSQueue() {
    try {
      const data = await http.get('/inventory/unmapped-pos-queue');
      return data;
    } catch (error) {
      console.error('Error fetching unmapped POS queue:', error);
      return { success: false, categories: [], has_unmapped: false };
    }
  }

  /**
   * Assign a master category to an unmapped POS item.
   *
   * @param {number} queueId
   * @param {number} masterCategoryId
   */
  static async assignUnmappedCategory(queueId, masterCategoryId) {
    try {
      const data = await http.post(`/inventory/unmapped-pos-queue/${queueId}/assign`, {
        master_category_id: masterCategoryId
      });
      if (!data.success) throw new Error(data.error || 'Failed to assign category');
      return data;
    } catch (error) {
      console.error('Error assigning unmapped category:', error);
      throw error;
    }
  }

  /**
   * Manually trigger a UIRA daily snapshot for today (admin/testing use).
   *
   * @param {string} [date] – ISO date 'YYYY-MM-DD', defaults to today IST
   */
  static async triggerUIRASnapshot(date) {
    try {
      const data = await http.post('/inventory/uira-trigger-snapshot', date ? { date } : {});
      return data;
    } catch (error) {
      console.error('Error triggering UIRA snapshot:', error);
      throw error;
    }
  }
}

export default InventoryService;

